import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, ShieldCheck, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SectionLabel } from "../components/Shared";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT_HEADER_IMG = "https://static.prod-images.emergentagent.com/jobs/c287c074-9c17-4a33-a8c6-97638ad368e7/images/36a6757f7b9f06e1527bbbcf696df9e54c0caf7db0c8285e8dd7e147250c5866.png";

const initialForm = { name: "", email: "", phone: "", message: "" };

export default function Contacte() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Contacte — Orbis Forum Català";
  }, []);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Cal completar el nom, el correu i el missatge.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Missatge enviat correctament.");
      setSent(true);
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No s'ha pogut enviar el missatge. Torneu-ho a provar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={CONTACT_HEADER_IMG} alt="" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-ofc-black via-ofc-black/85 to-ofc-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-ofc-black via-transparent to-ofc-black/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel>Contacte</SectionLabel>
            <h1 className="font-serif-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] mt-6 max-w-3xl text-balance">
              Iniciem una conversa reservada
            </h1>
            <p className="mt-8 text-base md:text-lg text-white/60 font-light leading-relaxed max-w-2xl">
              Expliqueu-nos els vostres objectius internacionals. Us respondrem amb la
              màxima discreció i valorarem com podem acompanyar-vos.
            </p>
          </Reveal>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* FORMULARI */}
            <Reveal className="lg:col-span-7">
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-10">
                <div>
                  <label htmlFor="name" className="text-xs text-white/50 uppercase tracking-[0.2em] mb-3 block">
                    Nom i cognoms *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    data-testid="contact-name"
                    placeholder="El vostre nom"
                    className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-3 text-white placeholder-white/25 focus:outline-none focus:border-ofc-gold transition-colors"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div>
                    <label htmlFor="email" className="text-xs text-white/50 uppercase tracking-[0.2em] mb-3 block">
                      Correu electrònic *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      data-testid="contact-email"
                      placeholder="correu@exemple.com"
                      className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-3 text-white placeholder-white/25 focus:outline-none focus:border-ofc-gold transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="text-xs text-white/50 uppercase tracking-[0.2em] mb-3 block">
                      Telèfon
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      data-testid="contact-phone"
                      placeholder="+34 ..."
                      className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-3 text-white placeholder-white/25 focus:outline-none focus:border-ofc-gold transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="text-xs text-white/50 uppercase tracking-[0.2em] mb-3 block">
                    Missatge *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    data-testid="contact-message"
                    placeholder="Expliqueu-nos breument el vostre projecte"
                    className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-3 text-white placeholder-white/25 focus:outline-none focus:border-ofc-gold transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-3 bg-ofc-gold text-ofc-black px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-ofc-goldhover transition-colors duration-300 disabled:opacity-50 group"
                >
                  {loading ? "Enviant..." : "Enviar sol·licitud"}
                  {!loading && <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />}
                </button>

                {sent && (
                  <p data-testid="contact-success" className="text-sm text-ofc-gold font-light">
                    Gràcies. Hem rebut la vostra sol·licitud i us respondrem amb la màxima discreció.
                  </p>
                )}
              </form>
            </Reveal>

            {/* INFO */}
            <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
              <div className="border-t border-white/10 pt-8 space-y-10">
                <div className="flex items-start gap-4">
                  <ShieldCheck size={18} strokeWidth={1.5} className="text-ofc-gold mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Confidencialitat</p>
                    <p className="text-sm text-white/60 font-light leading-relaxed">
                      Tota la informació es tracta amb absoluta reserva i únicament amb les
                      persones estrictament necessàries.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin size={18} strokeWidth={1.5} className="text-ofc-gold mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Abast</p>
                    <p className="text-sm text-white/60 font-light leading-relaxed">
                      Servei exclusiu per a ciutadans catalans i empreses registrades a Catalunya,
                      amb projecció internacional.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
